import { FormNumberField, FormStarRating } from '@mfw/forms';
import Typography from '@mui/material/Typography';
import { useIntl } from 'react-intl';
import Box from '@mui/material/Box';

import type { ApiMetricDefinition } from '../../api/types/stats.types';
import type { MetricUnitSettings } from '../../utils/metricUnits';

import { getMetricMessage } from './metric-messages';
import { displayUnit } from '../../utils';
import { StressSlider } from './StressSlider';
import { MoodPicker } from './MoodPicker';

export function dailyMetricFieldTestId(prefix: string, key: string) {
  return `${prefix}-${key.replace(/_/g, '-')}`;
}

/**
 * One registry-driven field: label (+ unit suffix), and the right widget —
 * star rating for `valueKind: 'scale'`, the emoji MoodPicker for `mood`, the
 * 1–100 StressSlider for `stress_overall`, else a plain number field. Shared
 * by DailyEntryTab (the full backfill-capable form) and the Daily Activity
 * entry modal, so the non-numeric fields render identically on both.
 */
export const DailyMetricField = ({
  testIdPrefix = 'stats-daily',
  settings,
  def,
}: {
  settings: MetricUnitSettings;
  def: ApiMetricDefinition;
  testIdPrefix?: string;
}) => {
  const { formatMessage } = useIntl();
  const message = getMetricMessage(def.key);
  const baseLabel = message ? formatMessage(message) : def.key;
  const unit = displayUnit(def, settings);
  const label = unit ? `${baseLabel} (${unit})` : baseLabel;

  if (def.valueKind === 'scale') {
    return (
      <Box>
        <Typography color="text.secondary" variant="body2">
          {label}
        </Typography>
        <FormStarRating
          data-testid={dailyMetricFieldTestId(testIdPrefix, def.key)}
          name={def.key}
        />
      </Box>
    );
  }
  if (def.key === 'mood') {
    return <MoodPicker name={def.key} />;
  }
  if (def.key === 'stress_overall') {
    return <StressSlider name={def.key} />;
  }
  return (
    <FormNumberField
      data-testid={dailyMetricFieldTestId(testIdPrefix, def.key)}
      name={def.key}
      label={label}
    />
  );
};

export default DailyMetricField;
